import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { getMarginStyle, getPaddingStyle } from "core/styles/paddingMarginStyle";
import { ButtonProps, ButtonSize, ButtonVariant } from ".";
import { DesignStyle } from "../";

const sizing = {
  spinIcon: {
    size: {
      s: "14px",
      m: "14px",
      l: "16px",
      xl: "18px",
    },
    margin: {
      s: "4px 0",
      m: "4px 0",
      l: "6px 0",
      xl: "8px 0",
    },
  },

  button: {
    fontSize: {
      s: "12px",
      l: "16px",
      xl: "18px",
      m: "14px",
    },
    lineHeight: {
      s: "20px",
      l: "28px",
      xl: "32px",
      m: "24px",
    },

    padding: {
      s: "4px 16px",
      m: "4px 16px",
      l: "6px 16px",
      xl: "8px 32px",
    },
  },
} as const;

const disabledStyle = css`
  color: ${({ theme }) => theme?.colors?.neutral.neutral60};
  background-color: ${({ theme }) => theme?.colors?.neutral.neutral30};
  border: ${({ theme }) => `1px solid ${theme?.colors?.neutral.neutral40}`};
  box-shadow: unset;

  &:hover {
    color: ${({ theme }) => theme?.colors?.neutral?.neutral60};
    background-color: ${({ theme }) => theme?.colors?.neutral?.neutral30};
    border: ${({ theme }) => `1px solid ${theme?.colors?.neutral?.neutral40}`};
  }
`;

const buttonStyles = ({ variant = ButtonVariant.PRIMARY, disabled }: ButtonProps) => {
  const styleList = {
    [ButtonVariant.PRIMARY]: css`
      color: ${({ theme }) => theme?.colors?.white};
      background-color: ${({ theme }) => theme?.colors?.primary.default};
      border: 1px solid transparent;

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.primary.hover};
      }

      &:focus {
        background-color: ${({ theme }) => theme?.colors?.primary.hover};
        border: ${({ theme }) => `1px solid ${theme?.colors?.primary.focus}`};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.SECONDARY]: css`
      color: ${({ theme }) =>
        theme?.colors?.secondary?.isLight
          ? theme?.colors?.neutral.neutral90
          : theme?.colors?.white};
      background-color: ${({ theme }) => theme?.colors?.secondary.default};
      border: 1px solid transparent;

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.secondary.hover};
      }

      &:focus {
        background-color: ${({ theme }) => theme?.colors?.secondary.hover};
        border: ${({ theme }) => `1px solid ${theme?.colors?.secondary.focus}33`};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.DANGER]: css`
      color: ${({ theme }) => theme?.colors?.white};
      background-color: ${({ theme }) => theme?.colors?.danger.default};
      border: 1px solid transparent;

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.dangerv2.hover};
      }

      &:focus {
        background-color: ${({ theme }) => theme?.colors?.dangerv2.hover};
        border: ${({ theme }) => `1px solid ${theme?.colors?.dangerv2.focus}`};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.TEXT]: css`
      color: ${({ theme }) => theme?.colors?.neutral.neutral90};
      background-color: transparent;
      border: 1px solid transparent;
      box-shadow: unset;

      &:hover {
        background-color: transparent;
        color: ${({ theme }) => theme?.colors?.primary.hover};
      }

      &:focus {
        background-color: transparent;
        color: ${({ theme }) => theme?.colors?.primary.hover};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.TEXT_SECONDARY]: css`
      color: ${({ theme }) => theme?.colors?.neutral.neutral60};
      background-color: transparent;
      border: 1px solid transparent;
      box-shadow: unset;

      &:hover {
        background-color: transparent;
        color: ${({ theme }) => theme?.colors?.neutral.neutral60};
      }

      &:focus {
        background-color: transparent;
        color: ${({ theme }) => theme?.colors?.neutral.neutral60};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.OUTLINED]: css`
      color: ${({ theme }) => theme?.colors?.neutral.neutral100};
      background-color: ${({ theme }) => theme?.colors?.white};
      border: ${({ theme }) => `1px solid ${theme?.colors?.neutral.neutral40}`};

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.neutral.neutral20};
      }

      &:focus {
        background-color: ${({ theme }) => theme?.colors?.white};
        border: ${({ theme }) => `1px solid ${theme?.colors?.primary.focus}`};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.OUTLINED_2]: css`
      color: ${({ theme }) => theme?.colors?.neutral.neutral90};
      background-color: ${({ theme }) => theme?.colors?.neutral.neutral10};
      border: 1px solid ${({ theme }) => theme?.colors?.neutral.neutral90};

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.neutral.neutral20};
      }

      &:focus {
        background-color: ${({ theme }) => theme?.colors?.neutral.neutral20};
        border: ${({ theme }) => `1px solid ${theme?.colors?.neutral.neutral90}`};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.SECONDARY_OUTLINED]: css`
      color: ${({ theme }) => theme?.colors?.secondary?.default};
      background-color: ${({ theme }) => theme?.colors?.white};
      border: 1px solid ${({ theme }) => theme.colors.secondary.default};

      &:hover {
        background-color: ${({ theme }) => theme.colors.secondary.default};
        color: ${({ theme }) => theme.colors.white};
      }

      &:focus {
        background-color: ${({ theme }) => theme.colors.secondary.default};
        color: ${({ theme }) => theme.colors.white};
      }

      ${disabled && disabledStyle}
    `,
    [ButtonVariant.PRIMARY_LIGHT]: css`
      color: ${({ theme }) => theme?.colors?.primary.default};
      background-color: ${({ theme }) => theme?.colors?.primary.surface};
      border: 1px solid transparent;

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.primary.surface};
        color: ${({ theme }) => theme?.colors?.primary.hover};
      }

      &:focus {
        color: ${({ theme }) => theme?.colors?.primary.default};
        border: ${({ theme }) => `1px solid ${theme?.colors?.primary.focus}`};
      }

      ${disabled && disabledStyle}
    `,
  };

  return styleList[variant];
};

export const ButtonStyle = styled.button<ButtonProps>`
  font-family: ${({ theme }) =>
    theme.designStyle === DesignStyle.PROFESSIONAL ? "Rubik-Bold" : "Nunito-Sans-Bold"};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;

  border-radius: ${({ rounded, shape, theme }) =>
    rounded
      ? "60px"
      : shape === "square" || theme.designStyle === DesignStyle.PROFESSIONAL
      ? "0px"
      : "8px"};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  width: ${({ width }) => width || "fit-content"};
  height: ${({ height }) => height ?? "fit-content"};
  white-space: ${({ whiteSpace }) => whiteSpace};
  font-weight: ${({ weight }) => weight || 400};

  padding: ${({ size }) => sizing.button.padding?.[size]};
  font-size: ${({ fontSize, size }) =>
    fontSize ? `${fontSize}px` : sizing.button.fontSize?.[size]};
  line-height: ${({ lineHeight, size }) =>
    lineHeight ? `${lineHeight}px` : sizing.button.lineHeight?.[size]};

  box-shadow: ${({ theme }) => theme?.colors?.shadow.button};

  &:focus {
    outline: none;
  }

  ${buttonStyles}
  ${getPaddingStyle}
  ${getMarginStyle}
`;

export const ButtonLink = styled(Link)`
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: fit-content;
`;

export const SpinIcon = styled.img<{ size: ButtonSize }>`
  width: ${({ size }) => sizing.spinIcon.size?.[size] ?? "14px"};
  height: ${({ size }) => sizing.spinIcon.size?.[size] ?? "14px"};
  margin: ${({ size }) => sizing.spinIcon.margin?.[size] ?? "4px 0"};
`;
